import React, { useState, useEffect } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Plus, Users, Building2, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { CalendarEvent, Customer, Staff } from '../types';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface EventModalProps {
  event?: CalendarEvent;
  customers: Customer[];
  staff: Staff[];
  onClose: () => void;
  onSave: () => void;
  isNew?: boolean;
}

function EventModal({ event, customers, staff, onClose, onSave, isNew = false }: EventModalProps) {
  const [editedEvent, setEditedEvent] = useState<Partial<CalendarEvent>>(
    event || {
      title: '',
      description: '',
      start: new Date(),
      end: new Date(),
      staff_ids: []
    }
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!editedEvent.title?.trim()) {
        throw new Error('Title is required');
      }

      if (isNew) {
        const { data: newEvent, error: createError } = await supabase
          .from('calendar_events')
          .insert({
            title: editedEvent.title,
            description: editedEvent.description,
            start_date: editedEvent.start?.toISOString(),
            end_date: editedEvent.end?.toISOString(),
            customer_id: editedEvent.customer_id
          })
          .select()
          .single();

        if (createError) throw createError;

        if (editedEvent.staff_ids?.length) {
          const { error: staffError } = await supabase
            .from('calendar_event_staff')
            .insert(
              editedEvent.staff_ids.map(staffId => ({
                event_id: newEvent.id,
                staff_id: staffId
              }))
            );

          if (staffError) throw staffError;
        }
      } else if (event?.id) {
        const { error: updateError } = await supabase
          .from('calendar_events')
          .update({
            title: editedEvent.title,
            description: editedEvent.description,
            start_date: editedEvent.start?.toISOString(),
            end_date: editedEvent.end?.toISOString(),
            customer_id: editedEvent.customer_id
          })
          .eq('id', event.id);

        if (updateError) throw updateError;

        // Update staff assignments
        await supabase
          .from('calendar_event_staff')
          .delete()
          .eq('event_id', event.id);

        if (editedEvent.staff_ids?.length) {
          const { error: staffError } = await supabase
            .from('calendar_event_staff')
            .insert(
              editedEvent.staff_ids.map(staffId => ({
                event_id: event.id,
                staff_id: staffId
              }))
            );

          if (staffError) throw staffError;
        }
      }

      onSave();
    } catch (err) {
      console.error('Error saving event:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!event?.id) return;

    setLoading(true);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', event.id);

      if (deleteError) throw deleteError;

      onSave();
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const toggleStaff = (staffId: string) => {
    setEditedEvent(prev => ({
      ...prev,
      staff_ids: prev.staff_ids?.includes(staffId)
        ? prev.staff_ids.filter(id => id !== staffId)
        : [...(prev.staff_ids || []), staffId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {isNew ? 'New Event' : 'Edit Event'}
            </h2>
            {!isNew && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-3 py-2 text-red-600 hover:text-red-700 rounded-md hover:bg-red-50"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {showDeleteConfirm ? (
            <div className="space-y-4">
              <p className="text-gray-700">Are you sure you want to delete this event?</p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? 'Deleting...' : 'Delete Event'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={editedEvent.title}
                    onChange={(e) => setEditedEvent({ ...editedEvent, title: e.target.value })}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter event title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editedEvent.description}
                    onChange={(e) => setEditedEvent({ ...editedEvent, description: e.target.value })}
                    rows={3}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter event description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="datetime-local"
                      value={editedEvent.start?.toISOString().slice(0, 16)}
                      onChange={(e) => setEditedEvent({
                        ...editedEvent,
                        start: new Date(e.target.value)
                      })}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="datetime-local"
                      value={editedEvent.end?.toISOString().slice(0, 16)}
                      onChange={(e) => setEditedEvent({
                        ...editedEvent,
                        end: new Date(e.target.value)
                      })}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                  </label>
                  <select
                    value={editedEvent.customer_id || ''}
                    onChange={(e) => setEditedEvent({
                      ...editedEvent,
                      customer_id: e.target.value || undefined
                    })}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">No Customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Members
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {staff.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => toggleStaff(member.id)}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                          editedEvent.staff_ids?.includes(member.id)
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Users className="w-4 h-4 mr-1" />
                        {member.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Event'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function Calendar() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eventsResponse, customersResponse, staffResponse] = await Promise.all([
        supabase
          .from('calendar_events')
          .select(`
            *,
            customer:customers (
              id,
              name
            ),
            staff:calendar_event_staff (
              staff:staff (
                id,
                name
              )
            )
          `)
          .order('start_date'),
        supabase
          .from('customers')
          .select('*')
          .order('name'),
        supabase
          .from('staff')
          .select('*')
          .order('name')
      ]);

      if (eventsResponse.error) throw eventsResponse.error;
      if (customersResponse.error) throw customersResponse.error;
      if (staffResponse.error) throw staffResponse.error;

      const transformedEvents: CalendarEvent[] = eventsResponse.data.map(event => ({
        id: event.id,
        title: event.title,
        description: event.description,
        start: new Date(event.start_date),
        end: new Date(event.end_date),
        customer_id: event.customer_id,
        staff_ids: event.staff.map((s: any) => s.staff.id),
        created_at: event.created_at,
        updated_at: event.updated_at
      }));

      setEvents(transformedEvents);
      setCustomers(customersResponse.data);
      setStaff(staffResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    const customer = customers.find(c => c.id === event.customer_id);
    return {
      style: {
        backgroundColor: customer?.color || '#3B82F6',
        borderColor: customer?.color || '#3B82F6'
      }
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading calendar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Building2 className="w-8 h-8 text-indigo-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
          </div>
          <button
            onClick={() => setShowNewEvent(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Event
          </button>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 'calc(100vh - 250px)' }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={(event) => setSelectedEvent(event as CalendarEvent)}
          />
        </div>

        {(showNewEvent || selectedEvent) && (
          <EventModal
            event={selectedEvent || undefined}
            customers={customers}
            staff={staff}
            onClose={() => {
              setShowNewEvent(false);
              setSelectedEvent(null);
            }}
            onSave={() => {
              loadData();
              setShowNewEvent(false);
              setSelectedEvent(null);
            }}
            isNew={!selectedEvent}
          />
        )}
      </div>
    </div>
  );
}